import { RewardUnlockStrategy } from '@brandclub/types';
import { Box, Skeleton, Typography, styled, useMediaQuery } from '@mui/material';
import React from 'react';

import { ShoppingCartItem } from './type';
import {
  formatMoney,
  getAppConsistentParams,
  getEntityImage,
  getProductUrl,
  getPurchaseRewardAmountForProduct,
  resolveProductImageUrls,
} from '../../utils';
import { FallbackImage } from '../../Components';
import { RewardPillWithText } from '../components';
import { CampaignType, ProductRewardSearchResult } from '../../types';
import { QuantityControl } from './QuantityControl';
import { AmazonDeliveryMessage } from './AmazonDelivery';

const CartProductContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  textDecoration: 'none',
  padding: '20px 0',
  color: theme.vars.palette.text.primary,

  alignItems: 'center',
  gap: 15,

  '.product_image': {
    objectFit: 'contain',
    width: '92px',
    height: '104px',
  },

  '.product_detail': {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',

    width: '100%',
    gap: 6,
    '.info': {
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      [theme.breakpoints.down('sm')]: {
        gap: 4,
      },
      '.retailer_logo': {
        width: 24,
        height: 24,
        img: {
          width: 24,
          height: 24,
          objectFit: 'contain',
        },
      },
      '.price': {
        display: 'flex',
        alignItems: 'center',
        fontSize: 16,
        fontWeight: 600,
        gap: 13,
        [`theme.breakpoints.down('sm')`]: {
          gap: 11,
        },
      },
      '.name': {
        fontSize: 12,
        fontWeight: 500,
      },
    },
    '.bc-actions': {
      display: 'flex',
      gap: 50,
      marginTop: 5,
      alignItems: 'center',
      justifyContent: 'space-between',
      '.quantity': {},
      '.remove': {
        fontSize: 14,
        fontWeight: 500,
        '&:hover': {
          cursor: 'pointer',
          textDecoration: 'underline',
        },
      },
    },
  },

  '&.large': {
    gap: 30,
    [`theme.breakpoints.down('sm')`]: {
      gap: 25,
    },
    '.product_image': {
      width: 115,
      height: 115,
      [`theme.breakpoints.down('sm')`]: {
        width: '92px',
        height: '104px',
      },
    },
    '.product_detail': {
      gap: 10,
      [`theme.breakpoints.down('sm')`]: {
        gap: 6,
      },

      '.info': {
        '.price': {
          fontWeight: 600,
          fontSize: 16,
        },
        '.name': {
          fontWeight: 500,
          fontSize: 14,
        },
      },
      '.bc-actions': {
        justifyContent: 'space-between',

        '.remove': {
          fontWeight: 500,
          fontSize: 14,
          [`theme.breakpoints.down('sm')`]: {
            fontSize: 12,
          },
        },
      },
    },
  },
}));

export const CartProduct = ({
  large = false,
  product,
  className,
  disabled = false,
  editable = true,
  handleAddQuantity,
  handleMinusQuantity,
  cartProducts,
  storeBrandingType,
  prime,
}: {
  large?: boolean;
  product: ShoppingCartItem;
  /** Shows Amazon's Prime badge on this line. */
  prime?: { targetId: string; amazonSku: string };
  className?: string;
  disabled?: boolean;
  editable?: boolean;
  handleAddQuantity: (product: ShoppingCartItem) => void;
  handleMinusQuantity: (product: ShoppingCartItem) => void;
  cartProducts?: { [retailerSku: string]: ProductRewardSearchResult };
  storeBrandingType: string;
}) => {
  const cartProduct = cartProducts?.[product.stacklineSku];
  const metadata = cartProduct ?? (product as ShoppingCartItem & ProductRewardSearchResult);
  // hasActiveInstantReward on metadata is not always set, so we need to check the campaignRewards as well
  const hasActiveInstantReward =
    !!metadata?.hasActiveInstantReward ||
    (!!metadata?.campaignRewards &&
      metadata?.campaignRewards.some(
        (campaignReward) =>
          campaignReward?.campaignType === CampaignType.DailyDealsRewardCampaign ||
          (campaignReward &&
            'rewardUnlockStrategy' in campaignReward &&
            campaignReward.rewardUnlockStrategy === RewardUnlockStrategy.Instant),
      ));

  const rewardAmount = metadata && getPurchaseRewardAmountForProduct(metadata);
  const rewardPercent = !!metadata?.retailPrice && !!rewardAmount ? (rewardAmount * 100) / metadata.retailPrice : 0;

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const urlSearchParams = getAppConsistentParams(window.location.search);

  if (!metadata?.title) {
    return (
      <CartProductContainer className={'cart_product'}>
        <Skeleton className="product_image" />
        <Box className="product_detail">
          <Box className="info">
            <Skeleton className="price" />
            <Skeleton className="name" />
          </Box>
        </Box>
      </CartProductContainer>
    );
  }

  return (
    <CartProductContainer className={[large && 'large', className].filter(Boolean).join(' ') + ' cart_product'}>
      <a
        href={getProductUrl({
          title: metadata.title,
          stacklineSku: metadata.stacklineSku,
          urlSearchParams,
          dtcProductPageUrl: metadata.dtcProductPageUrl,
          storeType: storeBrandingType,
        })}
        style={{ textDecoration: 'none' }}
        target="_blank"
      >
        <Box>
          <FallbackImage
            title={metadata?.title ?? product.stacklineSku}
            className="product_image"
            {...resolveProductImageUrls(metadata)}
          />
        </Box>
      </a>
      <Box className={'product_detail'}>
        <Box className={'info'}>
          <Box className={'price'}>
            <Box className={'retailer_logo'}>
              <img
                src={metadata?.retailerShortLogo ?? getEntityImage(product.retailerId as number, 'retailer')}
                alt={`Logo for retailer: ${product.retailerId}`}
              />
            </Box>
            {formatMoney(metadata?.retailPrice ?? product.retailPrice)}
            {!!rewardAmount && (
              <RewardPillWithText
                rewardAmount={rewardAmount}
                rewardPercentText="reward"
                baseFontSize={isMobileView ? 12 : 14}
                pillContainerStyle={{
                  boxSizing: 'border-box',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                rewardPercent={rewardPercent}
                rewardCampaignType={
                  hasActiveInstantReward ? CampaignType.DailyDealsRewardCampaign : CampaignType.PurchaseRewardCampaign
                }
              />
            )}
          </Box>
          <Typography
            className={'name'}
            title={metadata?.title}
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            }}
          >
            {metadata?.title ?? 'Unknown Product'}
          </Typography>
          {prime && (
            <AmazonDeliveryMessage
              targetId={prime.targetId}
              context="cart"
              messageType="none"
              items={[{ amazonSku: prime.amazonSku, quantity: product.quantity }]}
            />
          )}
        </Box>
        {editable && (
          <Box className={'bc-actions'}>
            <Box>
              <QuantityControl
                large={large}
                disabled={disabled}
                count={product.quantity}
                onAddClick={() => handleAddQuantity(product)}
                onMinusClick={() => handleMinusQuantity(product)}
                trackedParams={product}
              />
            </Box>
          </Box>
        )}
      </Box>
    </CartProductContainer>
  );
};
